import React, { useState, useEffect, useMemo } from 'react';
import { Product, Location, ProductGroup } from 'utils/grocyApi.ts';
import { 
  useProductsQuery, 
  useLocationsQuery, 
  useProductGroupsQuery, 
  useUserSettingsQuery,
  useStockQuery
} from 'hooks/useQueries.ts';
import Dropdown from 'components/Dropdown.tsx';
import ProductPicker from 'components/ProductPicker.tsx';
import './AddItemModal.css';

interface AddItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddItem: (data: AddItemData) => void;
}

export interface AddItemData {
  productId?: number;
  productName?: string;
  amount: number;
  isNewProduct: boolean;
  newProductData?: {
    name: string;
    locationId: number;
    productGroupId: number;
    parentProductId?: number;
    noOwnStock: boolean;
  };
}

const AddItemModal: React.FC<AddItemModalProps> = ({ isOpen, onClose, onAddItem }) => {
  const { data: products = [] } = useProductsQuery();
  const { data: locations = [] } = useLocationsQuery();
  const { data: productGroups = [] } = useProductGroupsQuery();
  const { data: userSettings } = useUserSettingsQuery();
  const { data: stock = [] } = useStockQuery();

  const [searchTerm, setSearchTerm] = useState('');
  const [amount, setAmount] = useState(1);
  const [amountInputValue, setAmountInputValue] = useState('1');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showNewProductForm, setShowNewProductForm] = useState(false);
  const [newProductData, setNewProductData] = useState({
    name: '',
    locationId: 0,
    productGroupId: 0,
    parentProductId: 0,
    noOwnStock: false,
  });
  const [parentProductSearchTerm, setParentProductSearchTerm] = useState<string>('');
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  // Reset form state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setAmount(1);
      setAmountInputValue('1');
      setSelectedProduct(null);
      setShowNewProductForm(false);
      setNewProductData({
        name: '',
        locationId: 0,
        productGroupId: 0,
        parentProductId: 0,
        noOwnStock: false,
      });
      setParentProductSearchTerm('');
      setIsKeyboardOpen(false);
    } else {
      // Reset keyboard state and scroll position when modal closes
      setIsKeyboardOpen(false);
      // Scroll back to top of the page smoothly
      setTimeout(() => {
        globalThis.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  }, [isOpen]);

  const parsePositive = (value: string): number | null => {
    if (value.trim() === '') return null;
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return parsed;
  };

  const handleAmountInputChange = (value: string) => {
    setAmountInputValue(value);
    const parsed = parsePositive(value);
    if (parsed !== null) {
      setAmount(parsed);
    }
  };

  const commitAmountInput = () => {
    const nextAmount = parsePositive(amountInputValue) ?? amount;
    setAmount(nextAmount);
    setAmountInputValue(String(nextAmount));
  };

  useEffect(() => {
    setAmountInputValue(String(amount));
  }, [amount]);

  // Handle virtual keyboard detection and input scrolling
  useEffect(() => {
    const handleFocus = (e: FocusEvent) => {
      // Only handle focus events for inputs within our modal
      if (!isOpen || !(e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement)) {
        return;
      }
      
      const modalElement = document.querySelector('.add-item-modal');
      if (!modalElement?.contains(e.target)) {
        return;
      }

      setIsKeyboardOpen(true);
      // Scroll the focused element into view with some offset, but only within the modal
      setTimeout(() => {
        const target = e.target as HTMLElement | null;
        target?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        });
      }, 300); // Delay to allow keyboard animation
    };

    const handleBlur = (e: FocusEvent) => {
      // Only handle blur events for inputs within our modal
      if (!isOpen || !(e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement)) {
        return;
      }
      
      const modalElement = document.querySelector('.add-item-modal');
      if (!modalElement?.contains(e.target)) {
        return;
      }

      // Use a timeout to check if focus moved to another input in the modal
      setTimeout(() => {
        const activeElement = document.activeElement;
        const stillInModal = modalElement?.contains(activeElement);
        if (!stillInModal) {
          setIsKeyboardOpen(false);
        }
      }, 100);
    };

    // Detect viewport height changes (keyboard open/close)
    const handleResize = () => {
      if (!isOpen) return;
      
      const viewportHeight = globalThis.visualViewport?.height || globalThis.innerHeight;
      const screenHeight = globalThis.screen.height;
      const keyboardThreshold = screenHeight * 0.75; // Assume keyboard if viewport < 75% of screen
      
      setIsKeyboardOpen(viewportHeight < keyboardThreshold);
    };

    if (isOpen) {
      document.addEventListener('focusin', handleFocus);
      document.addEventListener('focusout', handleBlur);
      globalThis.visualViewport?.addEventListener('resize', handleResize);
      globalThis.addEventListener('resize', handleResize);
    }

    return () => {
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('focusout', handleBlur);
      globalThis.visualViewport?.removeEventListener('resize', handleResize);
      globalThis.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  // Set default values from user settings
  useEffect(() => {
    if (userSettings && showNewProductForm) {
      setNewProductData(prev => ({
        ...prev,
        locationId: prev.locationId || Number(userSettings.product_presets_location_id) || 0,
        productGroupId: prev.productGroupId || Number(userSettings.product_presets_product_group_id) || 0,
      }));
    }
  }, [userSettings, showNewProductForm]);

  // Create stock map for quick lookup
  const _stockMap = useMemo(() => {
    const map: Record<number, { product_id: number; amount: number }> = {};
    stock.forEach((item: { product_id: number; amount: number }) => {
      map[item.product_id] = item;
    });
    return map;
  }, [stock]);

  // Filter and sort products
  const _filteredProducts = useMemo(() => {
    if (!searchTerm.trim()) return [];

    const filtered = products.filter(product =>
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort with no_own_stock: 1 products at the top
    return filtered.sort((a, b) => {
      if (a.no_own_stock === 1 && b.no_own_stock !== 1) return -1;
      if (a.no_own_stock !== 1 && b.no_own_stock === 1) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [products, searchTerm]);

  const handleParentProductSelect = (product: Product) => {
    setNewProductData((prev) => ({ ...prev, parentProductId: product.id }));
    setParentProductSearchTerm(''); // Clear search term to hide list
  };

  const handleParentProductClear = () => {
    setNewProductData((prev) => ({ ...prev, parentProductId: 0 }));
    setParentProductSearchTerm('');
  };

  const handleProductSelect = (product: Product) => {
    setSelectedProduct(product);
    setShowNewProductForm(false);
    setSearchTerm(product.name);
  };

  const handleCreateNewProduct = () => {
    setShowNewProductForm(true);
    setSelectedProduct(null);
    setNewProductData(prev => ({
      ...prev,
      name: searchTerm,
    }));
  };

  const handleSubmit = () => {
    if (selectedProduct) {
      // Adding existing product
      onAddItem({
        productId: selectedProduct.id,
        amount,
        isNewProduct: false,
      });
    } else if (showNewProductForm && newProductData.name && newProductData.locationId && newProductData.productGroupId) {
      // Creating new product
      onAddItem({
        productName: newProductData.name,
        amount,
        isNewProduct: true,
        newProductData: {
          name: newProductData.name,
          locationId: newProductData.locationId,
          productGroupId: newProductData.productGroupId,
          parentProductId: newProductData.parentProductId || undefined,
          noOwnStock: newProductData.noOwnStock,
        },
      });
    }
  };

  const canSubmit = selectedProduct || (showNewProductForm && newProductData.name && newProductData.locationId && newProductData.productGroupId);

  if (!isOpen) return null;

  return (
    <div className="add-item-modal-overlay" onClick={onClose}>
      <div className={`add-item-modal ${isKeyboardOpen ? 'add-item-modal--keyboard-open' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="add-item-modal__header">
          <h2>Add Item to Shopping List</h2>
          <button type="button" onClick={onClose} className="add-item-modal__close">×</button>
        </div>

        <div className="add-item-modal__body">
          <div className="add-item-modal__field">
            <ProductPicker
              onSelect={handleProductSelect}
              searchTerm={searchTerm}
              onSearchTermChange={(term: string) => {
                setSearchTerm(term);
                setSelectedProduct(null);
                setShowNewProductForm(false);
              }}
              showCreateNew
              onCreateNew={() => handleCreateNewProduct()}
              autoFocus
              showList={Boolean(searchTerm && !showNewProductForm && !selectedProduct)}
            />
          </div>

          {/* New Product Form */}
          {showNewProductForm && (
            <div className="add-item-modal__new-product-form">
              <h3>Create "{searchTerm}"</h3>

              <Dropdown
                label="Location:"
                value={newProductData.locationId}
                onChange={(val: number) =>
                  setNewProductData((prev) => ({ ...prev, locationId: Number(val) }))
                }
                options={locations.map((location: Location) => ({
                  value: Number(location.id),
                  label: String(location.name),
                }))}
                placeholder="Select location..."
                placeholderValue={0}
                valueType="number"
                containerClassName="add-item-modal__field"
                selectClassName="add-item-modal__select"
              />

              <Dropdown
                label="Product Group:"
                value={newProductData.productGroupId}
                onChange={(val: number) =>
                  setNewProductData((prev) => ({ ...prev, productGroupId: Number(val) }))
                }
                options={productGroups.map((group: ProductGroup) => ({
                  value: Number(group.id),
                  label: String(group.name),
                }))}
                placeholder="Select product group..."
                placeholderValue={0}
                valueType="number"
                containerClassName="add-item-modal__field"
                selectClassName="add-item-modal__select"
              />

              <div className="add-item-modal__field">
                <label>Parent Product:</label>
                <div className="add-item-modal__parent-product-picker">
                  {!(newProductData.parentProductId > 0) && (
                    <ProductPicker
                      searchTerm={parentProductSearchTerm}
                      onSearchTermChange={setParentProductSearchTerm}
                      onSelect={handleParentProductSelect}
                      sortMode="parent-selection"
                      showList={Boolean(parentProductSearchTerm)}
                    />
                  )}
                  {newProductData.parentProductId > 0 && (
                    <div className="add-item-modal__selected-parent">
                      {products?.find(p => p.id === newProductData.parentProductId)?.name || 'Selected product'}
                    </div>
                  )}
                  {newProductData.parentProductId > 0 && (
                    <button
                      type="button"
                      onClick={handleParentProductClear}
                      className="add-item-modal__clear-parent"
                      title="Clear parent product"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>

              <div className="add-item-modal__field add-item-modal__checkbox-field">
                <label>
                  <input
                    type="checkbox"
                    checked={newProductData.noOwnStock}
                    onChange={(e) => setNewProductData(prev => ({ ...prev, noOwnStock: e.target.checked }))}
                  />
                  Mark as "No Own Stock" product
                </label>
              </div>
            </div>
          )}

          {/* Amount Selection */}
          {(selectedProduct || showNewProductForm) && (
            <div className="add-item-modal__field">
              <label>Amount:</label>
              <div className="add-item-modal__amount-container">
                <input
                  type="number"
                  value={amountInputValue}
                  onChange={(e) => handleAmountInputChange(e.target.value)}
                  onBlur={commitAmountInput}
                  min="1"
                  inputMode="decimal"
                  className="add-item-modal__amount-input"
                />
                <div className="add-item-modal__amount-buttons">
                  <button
                    type="button"
                    onClick={() => setAmount(amount + 1)}
                    className="add-item-modal__amount-btn"
                  >
                    +
                  </button>
                  <button
                    type="button"
                    onClick={() => setAmount(Math.max(1, amount - 1))}
                    className="add-item-modal__amount-btn"
                    disabled={amount <= 1}
                  >
                    −
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="add-item-modal__footer">
          <button type="button" onClick={onClose} className="add-item-modal__cancel-btn">
            Cancel
          </button>
          <button 
            type="button"
            onClick={handleSubmit} 
            disabled={!canSubmit}
            className="add-item-modal__add-btn"
          >
            Add to List
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddItemModal;
